/**
 * Display Name Validation Utility
 * Prevents spam registrations with hash-like or bot-generated display names
 */

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validates display names to prevent spam and hash-like bot names
 */
export function validateDisplayName(name: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!name || typeof name !== "string") {
    errors.push("Display name is required");
    return { isValid: false, errors, warnings };
  }

  const trimmed = name.trim();

  // Length validation
  if (trimmed.length < 2) {
    errors.push("Display name must be at least 2 characters long");
  }
  if (trimmed.length > 50) {
    errors.push("Display name must not exceed 50 characters");
  }

  // Pattern checks
  if (isHashLikePattern(trimmed)) {
    errors.push(
      "Display name looks like a random string. Please use a real name or business name."
    );
  }

  if (isOnlyNumbers(trimmed)) {
    errors.push("Display name cannot be only numbers");
  } else if (isBotPattern(trimmed)) {
    errors.push(
      "This display name pattern is not allowed. Please use a more descriptive name."
    );
  }

  if (!hasAtLeastOneLetter(trimmed)) {
    errors.push("Display name must contain at least one letter");
  }

  // Content validation
  if (hasExcessiveSpecialCharacters(trimmed)) {
    errors.push("Display name has too many special characters");
  }

  if (hasConsecutiveDuplicates(trimmed)) {
    warnings.push(
      "Display name has repeated characters. Consider a different name."
    );
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Detects hash-like patterns (random alphanumeric strings)
 * Examples: "JGgPiuvpNfwHDNmaYjz", "a1b2c3d4e5f6g7h8i9j0"
 */
function isHashLikePattern(name: string): boolean {
  // Pattern: Mostly alphanumeric with mix of upper/lowercase, no spaces, no natural words
  const alphanumericOnly = /^[a-zA-Z0-9]+$/.test(name);
  if (!alphanumericOnly) return false;

  // Length 12-30 chars is typical for generated hashes
  if (name.length < 12 || name.length > 30) return false;

  // Check entropy: mix of upper and lower case suggests random
  const hasUpper = /[A-Z]/.test(name);
  const hasLower = /[a-z]/.test(name);
  const hasNumbers = /[0-9]/.test(name);
  const entropyCount = [hasUpper, hasLower, hasNumbers].filter(Boolean).length;

  // High entropy (upper + lower, or any combo of 2+ types) + alphanumeric only + mid-length = likely hash
  if (entropyCount >= 2) {
    // Additional check: does it contain common words as separate words (not substrings)?
    // Split by transitions from letters to numbers or case changes
    const commonWords = [
      "the",
      "and",
      "for",
      "you",
      "but",
      "not",
      "all",
      "can",
      "her",
      "was",
      "one",
      "our",
      "out",
      "day",
      "get",
      "has",
      "him",
      "his",
      "how",
      "its",
      "may",
      "new",
      "now",
      "old",
      "see",
      "two",
      "way",
      "who",
      "boy",
      "did",
      "car",
      "let",
      "put",
      "say",
      "she",
      "too",
      "use",
      "john",
      "smith",
      "maria",
      "sarah",
      "james",
      "robert",
      "michael",
      "kitchen",
      "chef",
      "cafe",
      "deli",
      "food",
      "home",
      "made",
    ];

    const lowerName = name.toLowerCase();
    
    // Check for words with word boundary (start/end of string or transitions)
    // Words at boundaries or followed by uppercase (like camelCase)
    const hasCommonWord = commonWords.some((word) => {
      // Match word at start or after uppercase letter (CamelCase)
      const atStart = new RegExp(`^${word}[A-Z]|^${word}\\d`).test(name);
      // Match word at end
      const atEnd = new RegExp(`[A-Z]${word}$|\\d${word}$|^${word}$`).test(lowerName);
      // Match word surrounded by capitals (CamelCase word)
      const camelCase = new RegExp(`[A-Z]${word}[A-Z]|[A-Z]${word}\\d`).test(name);
      
      return atStart || atEnd || camelCase;
    });

    if (!hasCommonWord) {
      return true; // Likely a hash
    }
  }

  return false;
}

/**
 * Detects bot-like patterns
 * Examples: "user123", "test456", "admin789", "bot001", "spam99"
 */
function isBotPattern(name: string): boolean {
  const botPatterns = [
    /^(user|test|admin|bot|spam|fake|temp|demo|bot|account)\d+$/i,
    /^(user|test|admin|bot)\s*$/i,
    /^\d{3,}$/i, // Only numbers (3+ digits)
    /^[a-z]{1,3}\d{5,}$/i, // 1-3 letters followed by 5+ numbers
    /^(a|b|c|x|y|z)\d{2,}$/i, // Single letter + many numbers
  ];

  return botPatterns.some((pattern) => pattern.test(name.trim()));
}

/**
 * Checks for excessive special characters that indicate spam
 */
function hasExcessiveSpecialCharacters(name: string): boolean {
  // Reject pure symbol spam like "!!!###$$$%%%^^^&&&"
  if (/^[!@#$%^&*()+=\[\]{};:'",.<>/?\\|`~]{3,}$/.test(name)) {
    return true;
  }

  // For other strings, we want to allow:
  // - Latin letters (a-zA-Z)
  // - Numbers (0-9)
  // - Spaces, hyphens, apostrophes, periods
  // - International Unicode letters (Chinese, Arabic, etc.)
  
  // Count characters that are NOT alphanumeric/space/hyphen/apostrophe/period
  // and NOT valid letter characters in any script
  let problematicCount = 0;
  for (let i = 0; i < name.length; i++) {
    const char = name[i];
    const code = char.charCodeAt(0);
    
    // Check if character is alphanumeric, space, or allowed punctuation
    const isAllowedASCII = /[a-zA-Z0-9\s\-'.]/g.test(char);
    
    // Check if it's a letter in any language (Unicode category)
    // Most non-Latin letters have code >= 128
    const isUnicodeLetter = code >= 128;
    
    if (!isAllowedASCII && !isUnicodeLetter) {
      problematicCount++;
    }
  }
  
  // If more than 20% are truly problematic symbols, reject
  if (problematicCount / name.length > 0.2) {
    return true;
  }

  return false;
}

/**
 * Checks if name is only numbers
 */
function isOnlyNumbers(name: string): boolean {
  return /^\d+$/.test(name.trim());
}

/**
 * Checks if name contains at least one letter (Latin or Unicode)
 * Prevents names like "134234122314123423" or "!@#$%^&*()"
 */
function hasAtLeastOneLetter(name: string): boolean {
  // Check for Latin letters
  if (/[a-zA-Z]/.test(name)) {
    return true;
  }
  
  // Check for Unicode letters (code >= 128 typically indicates non-ASCII letters)
  for (let i = 0; i < name.length; i++) {
    const code = name.charCodeAt(i);
    // Most international letters are >= 128 (Latin extended, Cyrillic, Arabic, Chinese, etc.)
    if (code >= 128) {
      return true;
    }
  }
  
  return false;
}

/**
 * Checks for consecutive duplicate characters (e.g., "aabbccdd")
 */
function hasConsecutiveDuplicates(name: string): boolean {
  return /(.)\1{3,}/.test(name); // 4+ consecutive same characters
}

/**
 * Sanitizes display name by removing/replacing problematic characters
 */
export function sanitizeDisplayName(name: string): string {
  return (
    name
      .trim()
      // Replace multiple spaces with single space
      .replace(/\s+/g, " ")
      // Remove leading/trailing spaces
      .trim()
  );
}

/**
 * Validates business name for artisan registration
 */
export function validateBusinessName(name: string): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!name || typeof name !== "string") {
    // Business name is optional, so empty is okay
    return { isValid: true, errors, warnings };
  }

  const trimmed = name.trim();

  if (trimmed.length > 0) {
    if (trimmed.length < 3) {
      errors.push("Business name must be at least 3 characters long");
    }
    if (trimmed.length > 100) {
      errors.push("Business name must not exceed 100 characters");
    }

    // Reuse hash detection for business names too
    if (isHashLikePattern(trimmed)) {
      errors.push("Business name cannot be a random string");
    }

    if (hasExcessiveSpecialCharacters(trimmed)) {
      errors.push("Business name has too many special characters");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Check if email looks like a disposable/spam email
 */
export function isDisposableEmail(email: string): boolean {
  const disposableDomains = [
    "tempmail.com",
    "temp-mail.org",
    "10minutemail.com",
    "guerrillamail.com",
    "throwaway.email",
    "maildrop.cc",
    "mailinator.com",
    "temp.email",
    "trashmail.com",
    "yopmail.com",
    "fakeinbox.com",
    "sharklasers.com",
    "spam4.me",
    "tempmail.ninja",
    "mytrashmail.com",
  ];

  const domain = email.split("@")[1]?.toLowerCase();
  return disposableDomains.includes(domain || "");
}
